/**
 * t645u: below them, Channels matching the name or topic (GET /search type 'channels': yours, plus public ones you
 * can join; private ones only if you're in them; never DMs). Enter or click opens the channel (a preview with Join
 * if you're not in it).
 *
 * Top search bar people suggestions: as you type, close matches (fuzzy: prefix, word start, initials, letters in
 * order) with avatar, position and building. Up/Down to move, Enter to open the person drawer, Escape to close.
 * Enter with nothing highlighted still runs the full search. The drawer applies its own permissions.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Hash, Lock } from 'lucide-react';
import { usePeopleDirectory as usePeopleDirectoryQuery } from '@tradeoutlet/data';
import { moduleForSearchType, personMatchScore, type DirectoryPerson, type SearchResult } from '@tradeoutlet/shared';
import { api } from '@/lib/api';
import { useMembers, useWorkspaceId } from '@/lib/hooks';
import { useModuleOn } from '@/lib/modules';
import { useDebounced } from '@/lib/useDebounced';
import { Avatar } from '@/components/ui';

export type ChannelHit = SearchResult['channels'][number];
export const channelLabel = (c: ChannelHit) => `#${(c.slug ?? c.title ?? 'channel').replace(/^#/, '')}`;

/** t645u: channels matching the typed text (2+ characters), from the server's permission-checked search. */
export function useChannelSuggest(q: string, enabled: boolean, limit = 5): ChannelHit[] {
  const ws = useWorkspaceId();
  const on = useModuleOn(moduleForSearchType('channels'));
  const t = useDebounced(q.trim(), 150);
  const live = enabled && on && Boolean(ws) && t.replace(/^#+/, '').length >= 2;
  const r = useQuery({ queryKey: ['search', ws ?? '', 'channels-suggest', t, limit], queryFn: () => api.search(ws!, t, { type: 'channels', limit }), enabled: live, staleTime: 30_000 });
  return live && q.trim() ? (r.data?.channels ?? []).slice(0, limit) : [];
}

export function rankPeople(q: string, people: DirectoryPerson[], limit = 6): DirectoryPerson[] {
  return people.map(p => ({ p, s: personMatchScore(q, p) })).filter(x => x.s > 0)
    .sort((a, b) => b.s - a.s || a.p.display_name.localeCompare(b.p.display_name)).slice(0, limit).map(x => x.p);
}

export function usePeopleDirectory(enabled: boolean) {
  const ws = useWorkspaceId();
  return usePeopleDirectoryQuery(ws, enabled);
}

export function PeopleSuggest({ q, hits, channels = [], active, onHover, onPick, onPickChannel }: {
  q: string; hits: DirectoryPerson[]; channels?: ChannelHit[]; active: number; onHover: (i: number) => void; onPick: (p: DirectoryPerson) => void; onPickChannel?: (c: ChannelHit) => void;
}) {
  const { byId } = useMembers();
  const label = useMemo(() => `People and channels matching ${q}`, [q]);
  if (!hits.length && !channels.length) return null;
  const head = 'px-3 pb-1 pt-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500';
  return (
    <ul role="listbox" id="top-search-people" aria-label={label} data-testid="top-search-people"
      className="absolute left-0 right-0 top-full z-50 mt-1 overflow-hidden rounded-lg bg-white py-1 text-sm shadow-xl ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700">
      {hits.length > 0 && <li className={head} aria-hidden>People</li>}
      {hits.map((p, i) => (
        <li key={p.user_id} id={`top-search-person-${i}`} role="option" aria-selected={i === active} data-testid="top-search-person"
          onMouseEnter={() => onHover(i)} onMouseDown={e => { e.preventDefault(); onPick(p); }}
          className={`flex cursor-pointer items-center gap-2 px-3 py-1.5 ${i === active ? 'bg-slate-100 dark:bg-slate-700' : ''}`}>
          <Avatar size={26} name={p.display_name} id={p.user_id} url={byId.get(p.user_id)?.avatar_url} />
          <span className="min-w-0 flex-1">
            <span className="block truncate font-medium text-slate-800 dark:text-slate-100">{p.display_name}</span>
            <span className="block truncate text-xs text-slate-500">{[p.position ?? p.job_title, p.building].filter(Boolean).join(' · ') || 'Member'}</span>
          </span>
        </li>
      ))}
      {channels.length > 0 && <li className={`${head} ${hits.length ? 'mt-1 border-t border-slate-100 pt-1.5 dark:border-slate-700' : ''}`} aria-hidden data-testid="top-search-channels">Channels</li>}
      {channels.map((c, j) => {
        const i = hits.length + j;
        return (
          <li key={c.id} id={`top-search-channel-${j}`} role="option" aria-selected={i === active} data-testid="top-search-channel"
            onMouseEnter={() => onHover(i)} onMouseDown={e => { e.preventDefault(); onPickChannel?.(c); }}
            className={`flex cursor-pointer items-center gap-2 px-3 py-1.5 ${i === active ? 'bg-slate-100 dark:bg-slate-700' : ''}`}>
            <span className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-500 dark:bg-slate-700">
              {c.is_private ? <Lock className="h-3.5 w-3.5" /> : <Hash className="h-3.5 w-3.5" />}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium text-slate-800 dark:text-slate-100">{channelLabel(c)}</span>
              {c.description && <span className="block truncate text-xs text-slate-500">{c.description}</span>}
            </span>
            {!c.is_member && <span className="shrink-0 rounded border border-slate-300 px-1.5 text-xs font-medium text-slate-600 dark:border-slate-600 dark:text-slate-300" data-testid="top-search-channel-join">Join</span>}
          </li>
        );
      })}
    </ul>
  );
}
